import { combineReducers, Reducer } from 'redux';
import { History } from 'history';
import { connectRouter } from 'connected-react-router';
import _ from 'lodash';

import videos from './reducers/videos';
import channels from './reducers/channels';
import channelVideos from './reducers/channel-videos';
import configuration from './reducers/configuration';
import appSettings from './reducers/app-settings';
import currentChannelId from './reducers/current-channel-id';
import currentVideoId from './reducers/current-video-id';
import search from './reducers/search';
import channelInfo from './reducers/channel-info';
import firstChannelVideoId from './reducers/first-channel-video-id';
import shareOverlay from './reducers/share-overlay';
import videoUrlsCache from './reducers/video-urls-cache';
import selectedVideoId from './reducers/selected-video-id';
import player from './reducers/player';
import isVideosTouched from './reducers/is-videos-touched';
import videoCards from './reducers/video-cards';
import payment from './reducers/payment';
import lazyChannelVideos from './lazy-channel-videos/reducer';
import isLightbox from './reducers/is-lightbox';
import fullScreenModal from './reducers/full-screen-modal';
import comments from './reducers/comments';
import { reducer as hydratedData } from './hydrated-data/hydrated-data';
import playback from './reducers/playback';
import controllerState from './reducers/controller-state';
import isReinitializing from './reducers/reinitializing';
import { RootState } from './root-state';
import { RESET_FOR_REINIT } from './actions/reset-for-reinit';

// Slices preserved across a RESET_FOR_REINIT. Everything else re-inits to its own defaultState.
const PRESERVED_ON_REINIT: (keyof RootState)[] = [
  'router',
  'appSettings',
  'hydratedData',
  'controllerState',
  'configuration',
  'currentChannelId',
  'currentVideoId',
  // channelInfo + entities are preserved together on purpose: getMainVideoId() falls back to
  // channelInfo.featuredItem, and rendering that id reads entities.videos[featuredItem]. Preserving
  // one without the other leaves a dangling ref → render throws → error boundary blanks the widget
  // ("reinit never completes"). Kept together, the previous valid content renders until the new
  // data (fetched by setInitialState) overwrites these caches. The reset still clears the slices
  // that actually drive selection/pagination (lazyChannelVideos, selectedVideoId, player, search…).
  'channelInfo',
  'entities',
  'isReinitializing', // the gate flag itself must survive the reset that happens while it is on
];

export const createRootReducer: CreateRootReducer = ({ history }) => {
  const appReducer = combineReducers({
    router: connectRouter(history),
    search,
    entities: combineReducers({
      videos,
      channels,
    }),
    channelVideos,
    currentChannelId,
    currentVideoId,
    channelInfo,
    firstChannelVideoId,
    lazyChannelVideos,
    isLightbox,
    videoUrlsCache,
    selectedVideoId,
    player,
    isVideosTouched,
    payment,
    configuration,
    appSettings,
    videoCards,
    shareOverlay,
    fullScreenModal,
    comments,
    hydratedData,
    playback,
    controllerState,
    isReinitializing,
  });

  return (state: RootState | undefined, action: any) => {
    if (action.type === RESET_FOR_REINIT && state) {
      state = _.pick(state, PRESERVED_ON_REINIT) as RootState;
    }
    return appReducer(state as any, action);
  };
};

export default createRootReducer;

export type CreateRootReducer<S extends RootState = RootState> = (options: {
  history: History;
}) => Reducer<S>;
